import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import toast from '../../utils/toast';
import {
  palette,
  radius,
  shadow,
  spacing,
  typography,
  zIndex,
  layout,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import { useDialogFocus } from '../hooks/use-dialog-focus';
import { ModalPortal } from '../modal/modal-portal';
import { Presence, m, scrimMotion, useOverlayMotion } from '../../motion';
import { AppButton } from '../button/app-button';
import { StripeCardPanel } from '../payments/stripe-card-modal';
import { useUnitPurchase } from '../payments/use-unit-purchase';
import { RestoreUnitsPanel } from './restore-units-panel';
import { COMPED_ACCOUNT_MESSAGE } from '../../api/plan-client';

/* Portaled to <body> for the same reason ConfirmDialog is: an animated ancestor
 * creates a transform containing block that would trap a fixed overlay. */
const Scrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.md};
  background: ${palette.overlay};

  ${down('sm')} {
    align-items: flex-end;
    padding: 0;
  }
`;

const Card = m.div`
  width: 100%;
  max-width: 460px;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};
  padding: ${spacing.lg};

  ${down('sm')} {
    max-width: none;
    max-height: calc(100vh - ${spacing.sm});
    max-height: calc(100dvh - ${spacing.sm});
    overflow-y: auto;
    border-radius: ${radius.lg} ${radius.lg} 0 0;
    padding: ${spacing.md} ${spacing.sm};
  }
`;

const Title = styled.h2`
  margin: 0 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: -0.014em;
  color: ${palette.ink};
`;

const Message = styled.p`
  margin: 0 0 ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.md};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.surfaceMuted};
`;

const Stat = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};

  & b {
    display: block;
    font-size: ${typography.size.lg}px;
    font-weight: ${typography.weight.bold};
    color: ${palette.ink};
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.inkSoft};
`;

const Input = styled.input`
  width: 100%;
  height: ${layout.fieldHeight};
  margin-bottom: ${spacing.xs};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

const Note = styled.p`
  margin: 0 0 ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

/* The server's own refusal, shown verbatim. Same treatment as the billing
 * page's BuyNotice — one wording, decided by the server, on both doors. */
const Refusal = styled.p`
  margin: 0 0 ${spacing.md};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.tealWashSoft};
  color: ${palette.inkSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${spacing.xs};
`;

export interface BuyUnitsDialogProps {
  open: boolean;
  // What the caller was trying to do when it ran out, e.g. "allocate 40 units
  // to Riverside". Used to explain the interruption in the user's own terms.
  reason?: string | null;
  unitsAllowed: number | null;
  unitsUsed: number;
  // How many the blocked action needed. Pre-fills the field so the common case
  // is one click — the admin does not have to work out the shortfall themselves.
  shortfall?: number | null;
  // False for a property_manager: they hit the wall but cannot buy their way
  // out of it, so they get told who can instead of a button that would 403.
  canBuy: boolean;
  // A complimentary account: nobody can buy, so the dialog says the plan is
  // complimentary rather than "ask your account owner" (who cannot either).
  comped?: boolean;
  // Archived (previously returned) units the org holds. When > 0 a successful
  // purchase is followed by a RESTORE step offering them back — the newly
  // bought capacity can revive the original units instead of minting fresh
  // ones. 0 (or omitted) skips the step entirely.
  returnedCount?: number;
  onDismiss: () => void;
  // Fired after a successful purchase, so the caller can retry what failed.
  // `info.restored` is how many previously returned units the user brought
  // back in the restore step (0 when the step was skipped or absent): a caller
  // whose pending action was "create N fresh units" must NOT blindly retry it
  // when the capacity just went to restored units instead.
  onBought: (unitsAllowed: number, info?: { restored: number }) => void;
}

// The upsell. Raising capacity is a TOTAL, not a delta — the server takes the
// new total and prorates against Stripe — but nobody thinks in totals when they
// have just been stopped, so the field asks for "how many more" and the total is
// computed here.
//
// ---------------------------------------------------------------------------
// WHY THIS IS TWO STEPS NOW (WP-22)
// ---------------------------------------------------------------------------
// It used to call buyUnits() straight off the Add button, so the only money on
// screen was a sentence: "on a paid plan the change is prorated straight away;
// during your trial nothing is charged until the trial ends." That sentence was
// hardcoded, was the second-worst kind of price (a guess about someone else's
// subscription), and it was wrong for exactly the orgs it was reassuring — a
// card-less trial is CANCELLED at trial_end, not converted.
//
// It now shares useUnitPurchase with the billing page, so both doors quote the
// SAME server response and, when the server asks for a card, both collect it
// the same way. The card surface is rendered INLINE inside this dialog rather
// than as a second modal: a dialog inside a dialog is two scrims, two focus
// traps and two Escape handlers, and this component already owns all three.
export const BuyUnitsDialog: React.FC<BuyUnitsDialogProps> = ({
  open,
  reason,
  unitsAllowed,
  unitsUsed,
  shortfall,
  canBuy,
  comped = false,
  returnedCount = 0,
  onDismiss,
  onBought,
}) => {
  const suggested = Math.max(1, shortfall || 1);
  const [add, setAdd] = useState(String(suggested));
  const [step, setStep] = useState<'choose' | 'confirm' | 'restore'>('choose');
  // The purchase result the restore step reads and the exits report. Held in a
  // ref: nothing renders from it directly, and setState in finish() would race
  // the step change.
  const boughtRef = useRef<{ units_allowed: number; units_remaining: number } | null>(null);
  const busyRef = useRef(false);
  const purchase = useUnitPurchase();
  // Pulled out so the effect below can depend on the stable callback itself
  // rather than on the whole hook object, which changes identity every render.
  const resetPurchase = purchase.reset;

  // Re-arm when the dialog is reopened for a different shortfall. Without this
  // the field would still hold the previous action's number — and, since WP-22,
  // the previous action's QUOTE, which would be a price for a purchase nobody
  // asked for.
  useEffect(() => {
    if (open) {
      setAdd(String(Math.max(1, shortfall || 1)));
      setStep('choose');
      boughtRef.current = null;
      resetPurchase();
    }
  }, [open, shortfall, resetPurchase]);

  const handleDismiss = () => {
    if (busyRef.current || purchase.quoting) {
      return;
    }
    // Past the purchase, Escape/scrim must still report the buy — the caller
    // reloads the allowance off this callback, and the money HAS moved.
    if (step === 'restore' && boughtRef.current) {
      onBought(boughtRef.current.units_allowed, { restored: 0 });

      return;
    }
    onDismiss();
  };
  const dialogRef = useDialogFocus(open, handleDismiss);
  const cardMotion = useOverlayMotion('modal');

  const parsed = Number.parseInt(add, 10);
  const valid = Number.isInteger(parsed) && parsed > 0;
  const current = unitsAllowed ?? unitsUsed;
  const newTotal = useMemo(
    () => (valid ? current + parsed : current),
    [current, parsed, valid]
  );

  // Step 1 -> 2. Re-entry guarded for the same reason the billing row is: two
  // taps on a slow connection must not send two quotes.
  const getQuote = async () => {
    if (busyRef.current || !valid) {
      return;
    }

    busyRef.current = true;
    setStep('confirm');
    try {
      await purchase.quote(newTotal);
    } finally {
      busyRef.current = false;
    }
  };

  const finish = (result: {
    units_allowed: number;
    units_remaining: number;
    subscription_created?: boolean;
  }) => {
    toast.success(
      result.subscription_created
        ? 'Card saved and your subscription is active'
        : `Added ${parsed} unit${parsed === 1 ? '' : 's'} — ${
            result.units_remaining
          } available`
    );
    // The org holds previously returned units: offer them back BEFORE the
    // caller spends the new capacity on fresh ones. Restoring is not a toll
    // gate — "Create fresh units instead" and Escape both exit reporting the
    // purchase with restored: 0.
    if (returnedCount > 0 && result.units_remaining > 0) {
      boughtRef.current = result;
      setStep('restore');

      return;
    }
    onBought(result.units_allowed, { restored: 0 });
  };

  const available = Math.max(0, current - unitsUsed);
  const preview = purchase.preview;
  // Read the ref ONCE, into a const, so the restore panel and both of its
  // callbacks see the same value. The previous version reached through
  // `boughtRef.current!` inside each callback: the `!` was needed because a ref
  // can be reassigned between render and click, so TypeScript cannot narrow it
  // — and the assertion was silencing exactly the case it should have handled.
  // Capturing at render is also the behaviour that was intended: the panel is
  // reporting on the purchase it was rendered for.
  const bought = boughtRef.current;

  return (
    <Presence>
      {open ? (
    <ModalPortal key="buy-units-dialog">
    <Scrim {...scrimMotion} onClick={handleDismiss}>
      <Card
        {...cardMotion}
        ref={dialogRef}
        tabIndex={-1}
        role="alertdialog"
        aria-modal="true"
        aria-label="Add more units"
        onClick={e => e.stopPropagation()}
      >
        <Title>
          {!canBuy
            ? 'Out of units'
            : step === 'restore'
            ? 'Restore returned units?'
            : purchase.collectCard
            ? 'Add a card'
            : 'Add more units'}
        </Title>
        <Message>
          {step === 'restore' ? (
            <>
              You have {returnedCount} previously returned unit
              {returnedCount === 1 ? '' : 's'}. Your new capacity can bring
              them back exactly as they were — or you can create fresh units
              instead.
            </>
          ) : (
            <>
              {reason
                ? `${reason} needs more units than you own.`
                : 'You have used every unit on your plan.'}{' '}
              {canBuy
                ? purchase.collectCard
                  ? 'Your card is held by Stripe, never by Lobbero. Nothing is charged until you confirm below.'
                  : 'Units are what you are billed for, so adding capacity is the same as adding units to your plan.'
                : comped
                ? COMPED_ACCOUNT_MESSAGE
                : 'Ask your account owner to add units from Billing & plan.'}
            </>
          )}
        </Message>

        {step !== 'restore' && (
          <Stats>
            <Stat>
              <b>{unitsUsed}</b> in use
            </Stat>
            <Stat>
              <b>{unitsAllowed ?? 0}</b> owned
            </Stat>
            <Stat>
              <b>{available}</b> available
            </Stat>
          </Stats>
        )}

        {step === 'restore' && bought && (
          <RestoreUnitsPanel
            remaining={bought.units_remaining}
            skipLabel="Create fresh units instead"
            onSkip={() => onBought(bought.units_allowed, { restored: 0 })}
            onRestored={restored => onBought(bought.units_allowed, { restored })}
          />
        )}

        {canBuy && step === 'choose' && (
          <>
            <Label htmlFor="buy-units-count">How many more units?</Label>
            <Input
              id="buy-units-count"
              type="number"
              min={1}
              step={1}
              value={add}
              onChange={e => setAdd(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void getQuote();
                }
              }}
            />
            <Note>
              {valid
                ? `Your plan goes from ${current} to ${newTotal} units.`
                : 'Enter a whole number greater than zero.'}{' '}
              {/* No claim about money is made here any more. The next step asks
                  the server what this actually costs, and shows that. */}
              You will see the price before anything is charged.
            </Note>
          </>
        )}

        {canBuy && step === 'confirm' && purchase.quoting && (
          <Note>Getting a quote from Stripe…</Note>
        )}

        {canBuy && step === 'confirm' && !purchase.quoting && (
          <StripeCardPanel
            collectCard={purchase.collectCard}
            prepareIntent={purchase.prepareIntent}
            summaryRows={purchase.summaryRows}
            notice={
              purchase.refusal ? (
                <Refusal role="alert">{purchase.refusal}</Refusal>
              ) : null
            }
            onConfirmed={async result => {
              busyRef.current = true;
              try {
                finish(await purchase.confirmed(result));
              } finally {
                busyRef.current = false;
              }
            }}
            onSubmit={async () => {
              busyRef.current = true;
              try {
                finish(await purchase.submit());
              } finally {
                busyRef.current = false;
              }
            }}
            submitDisabled={purchase.blocked}
            submitLabel={
              preview
                ? purchase.collectCard
                  ? `Save card and add ${preview.delta} unit${
                      preview.delta === 1 ? '' : 's'
                    }`
                  : `Add ${preview.delta} unit${preview.delta === 1 ? '' : 's'}`
                : 'Add units'
            }
            busyLabel="Adding…"
            cancelLabel="Back"
            onCancel={() => {
              purchase.reset();
              setStep('choose');
            }}
            // QUANTITY_CHANGED is shown through `refusal`, which survives the
            // re-quote; this panel remounts across it and would lose its own
            // error line anyway.
            onError={purchase.handleCommitError}
            fallbackErrorMessage="Could not add units. Please try again."
          />
        )}

        {(!canBuy || step === 'choose') && (
          <Actions>
            <AppButton
              variant="ghost"
              disabled={purchase.quoting}
              onClick={handleDismiss}
            >
              {canBuy ? 'Not now' : 'Close'}
            </AppButton>
            {canBuy && (
              <AppButton
                variant="primary"
                disabled={purchase.quoting || !valid}
                onClick={getQuote}
              >
                Continue
              </AppButton>
            )}
          </Actions>
        )}
      </Card>
    </Scrim>
    </ModalPortal>
      ) : null}
    </Presence>
  );
};
